import { useEffect, useMemo, useState, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type { Client } from '@/types/database';
import {
  Users,
  Plus,
  Phone,
  MapPin,
  Search,
  Clock,
  FileSpreadsheet,
  ContactRound,
  ArrowUpDown,
  SlidersHorizontal,
  Check,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { useNavigate } from 'react-router-dom';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useXlsxExport } from '@/hooks/useXlsxExport';
import { buildClientsXlsx, type ClientQuoteRow } from '@/lib/xlsxTemplates';
import { BulkActionBar } from '@/components/bulk-actions/BulkActionBar';
import { BulkDeleteDialog } from '@/components/bulk-actions/BulkDeleteDialog';
import { ImportContactsModal } from '@/components/clients/ImportContactsModal';
import { cacheClients, getCachedClients } from '@/lib/offlineDataCache';
import { addToOfflineQueue, removeFromOfflineQueue } from '@/lib/offlineStorage';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRole } from '@/hooks/useRole';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CONTENT_CARD } from '@/components/ui/field';
import { StorageImage } from '@/components/ui/StorageImage';
import { plural } from '@/lib/plural';
import { formatDocument } from '@/lib/documentUtils';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE } from '@/lib/listFilters';
import {
  CLIENT_CONTACT_FILTERS,
  CLIENT_SORT_LABELS,
  CLIENT_SORT_ORDER,
  DEFAULT_CLIENT_SORT,
  clientListTotalLabel,
  clientMatchesSearch,
  countActiveClientFilters,
  countClientsByContact,
  matchesClientPeriod,
  matchesContactFilter,
  sortClientsBy,
  summarizeClientList,
  type ClientContactFilter,
  type ClientSortOption,
} from '@/lib/clientsListFilters';
import {
  PERIOD_PRESET_LABELS,
  resolvePeriodPreset,
  type ReportPeriodPreset,
} from '@/lib/reportPeriod';

/**
 * Presets de "quando este cliente entrou". Subconjunto EXPLÍCITO dos de
 * `reportPeriod`, pelo mesmo motivo do `QUOTE_PERIOD_PRESETS`: os longos de
 * relatório não ajudam a achar uma pessoa, e `custom` exigiria um calendário
 * dentro do modal.
 */
const CLIENT_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

const PERIOD_GROUP_KEY = 'period';

export default function ClientsPage() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const { isOnline, checkOnline } = useOfflineSync();
  // Team roles: members+ may create/edit; only owner/admin may delete.
  const { canWrite, canDelete } = useRole();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState<ClientSortOption>(DEFAULT_CLIENT_SORT);
  const [contactFilter, setContactFilter] = useState<ClientContactFilter>('all');
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  /**
   * MODO DE SELEÇÃO — desligado por padrão (#192).
   *
   * A caixa em todo card cobrava de quem só veio consultar a agenda o preço de
   * uma ação de exceção: ela roubava largura do NOME em cada linha, que é
   * justamente o que distingue duas pessoas na lista.
   */
  const [selectionMode, setSelectionMode] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: `resolvePeriodPreset` cria `Date`s a partir de "agora", e
   * chamá-la por cliente compararia cada linha contra um "agora" diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /** O recorte SEM o contato — é ele que alimenta as contagens dos botões. */
  const clientsBeforeContact = clients.filter(
    (client) => clientMatchesSearch(client, search) && matchesClientPeriod(client, periodRange),
  );

  const contactCounts = countClientsByContact(clientsBeforeContact);

  const filteredClients = sortClientsBy(
    clientsBeforeContact.filter((client) => matchesContactFilter(client, contactFilter)),
    sortOption,
  );

  const activeModalFilters = countActiveClientFilters(periodPreset);
  const recorteAtivo =
    search.trim().length > 0 || contactFilter !== 'all' || periodPreset !== null;
  const summary = summarizeClientList(filteredClients);

  const {
    selectedCount,
    allSelected,
    isSelected,
    toggleItem,
    clearSelection,
    toggleSelectAll,
    selectedIds,
  } = useBulkSelection(filteredClients);

  const { exportXlsx } = useXlsxExport();

  // Export selected (or all filtered) clients to XLSX. "Total gasto" is derived
  // by joining the user's approved/completed quotes on client_id.
  const handleExportXlsx = () => {
    const source =
      selectedCount > 0
        ? filteredClients.filter((c) => selectedIds.has(c.id))
        : filteredClients;

    exportXlsx(async () => {
      let quotesForSpend: ClientQuoteRow[] | undefined;
      if (isOnline && user && activeCompany) {
        try {
          const { data } = await supabase
            .from('quotes')
            .select('client_id, status, total')
            .eq('company_id', activeCompany.id);
          if (data) quotesForSpend = data as ClientQuoteRow[];
        } catch {
          // Non-fatal: export clients without computed totals.
        }
      }
      return buildClientsXlsx(source, quotesForSpend);
    }, 'clientes');
  };

  // Get effective user ID from user context or cached session
  const getEffectiveUserId = useCallback((): string | null => {
    if (user?.id) return user.id;

    try {
      const cachedSession = localStorage.getItem('orcamigo_cache_auth_session');
      if (cachedSession) {
        const sessionData = JSON.parse(cachedSession);
        if (sessionData?.data?.userId) {
          return sessionData.data.userId;
        }
      }
    } catch (e) {
      console.error('[ClientsPage] Error reading cached session:', e);
    }
    return null;
  }, [user]);

  // Load cached data immediately on mount (critical for offline app restart)
  useEffect(() => {
    const userId = getEffectiveUserId();

    if (userId) {
      const cached = getCachedClients(userId);
      if (cached !== null && cached.length > 0) {
        setClients(cached as Client[]);
        setLoading(false);
      }
    }

    // If still loading after cache attempt and offline, stop loading
    if (!isOnline) {
      setLoading(false);
    }
  }, [getEffectiveUserId, isOnline]);

  // Fetch from server when online - always try to fetch for latest data.
  // Re-runs on company switch (activeCompany.id) so lists stay company-scoped.
  useEffect(() => {
    if (!user) return;
    if (!isOnline) return;
    if (!activeCompany) return;

    fetchClients();
  }, [user, isOnline, activeCompany?.id]);

  // Listen for sync completion to refresh data
  useEffect(() => {
    if (!user) return;

    const handleClientsUpdated = () => {
      if (isOnline) {
        fetchClients();
      } else {
        // Reload from cache
        const cached = getCachedClients(user.id);
        if (cached) {
          setClients(cached as Client[]);
        }
      }
    };
    window.addEventListener('clients-updated', handleClientsUpdated);

    return () => {
      window.removeEventListener('clients-updated', handleClientsUpdated);
    };
  }, [user, isOnline]);

  // Clear selection when the RECORTE changes. A ORDENAÇÃO fica de fora de
  // propósito: ela reorganiza as mesmas linhas, então a seleção continua
  // válida — limpá-la ali faria o usuário perder a marcação só por ter trocado
  // o critério.
  useEffect(() => {
    clearSelection();
  }, [search, contactFilter, periodPreset, clearSelection]);

  const fetchClients = async () => {
    if (!user || !activeCompany) return;

    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('company_id', activeCompany.id)
        .order('name');

      if (data && !error) {
        setClients(data as Client[]);
        // Cache for offline access
        cacheClients(user.id, data);
      } else {
        // Fallback to cache if database fetch fails (e.g. offline/network error)
        const cached = getCachedClients(user.id);
        if (cached) {
          setClients(cached as Client[]);
        }
      }
    } catch (e) {
      console.error('[ClientsPage] Error fetching clients:', e);
      // On error, keep cached data if available
    }
    setLoading(false);
  };

  const handleBulkDelete = async () => {
    if (!canDelete) {
      toast.warning('Apenas o proprietário e administradores podem excluir.');
      return;
    }
    setBulkDeleting(true);

    const idsToDelete = Array.from(selectedIds);

    const online = await checkOnline();
    if (!online && user) {
      const cached = (getCachedClients(user.id) || []) as any[];
      let next = cached;

      for (const clientId of idsToDelete) {
        if (String(clientId).startsWith('offline_')) {
          removeFromOfflineQueue(clientId);
        } else {
          addToOfflineQueue('delete_client', { clientId, userId: user.id });
        }
        next = next.filter((c) => c.id !== clientId);
      }

      cacheClients(user.id, next);
      setClients(next as Client[]);
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('clients-updated'));
      setBulkDeleting(false);
      toast.success(`${idsToDelete.length} cliente(s) excluído(s) localmente!`, { icon: '📴' });
      setBulkDeleteOpen(false);
      clearSelection();
      return;
    }

    const { error } = await supabase
      .from('clients')
      .delete()
      .in('id', idsToDelete);

    setBulkDeleting(false);

    if (error) {
      toast.error('Erro ao excluir clientes');
      return;
    }

    toast.success(`${idsToDelete.length} cliente(s) excluído(s)!`);
    setBulkDeleteOpen(false);
    clearSelection();
    fetchClients();
  };

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Cadastrado em',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...CLIENT_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: PERIOD_PRESET_LABELS[preset],
        })),
      ],
    },
  ];

  if (!user) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Users className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Área de clientes</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para gerenciar seus clientes
          </p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideFab={selectedCount > 0}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes e /quotes/new)  */}
        {/* ------------------------------------------------------------------ */}
        {/* Oitava tela no molde, e a segunda LISTA. O que precisou ser
            traduzido (o mesmo exercício do #182/#188):

            · O EYEBROW diz o "onde" — aqui, a família de que a lista faz parte.
            · O VALOR AO VIVO de uma lista de PESSOAS não é dinheiro: é quantas
              delas estão no recorte visível. O rótulo (`clientListTotalLabel`)
              é o que impede o número de ser lido como "tenho 3 clientes"
              quando ele é o resultado de uma busca.
            · A sublinha diz quantas dá para CHAMAR — é a informação acionável
              de uma agenda de contatos, e é o que dá sentido ao botão "Sem
              contato" logo abaixo.
            · Sem botão de voltar: `/clients` é destino da navegação inferior. */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Cadastros</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Clientes</h1>
            </div>

            {/* Ações SECUNDÁRIAS viram ícone (#186): três botões com texto não
                cabem na linha do título em 360px, e sem `flex-wrap` o excedente
                não quebra — ele sai da tela, levando junto a ação primária.
                `aria-label`/`title` são obrigatórios aqui, senão o botão perde
                o nome acessível junto com o texto (#142) — e a contagem da
                seleção vai neles, porque para o VIEWER (que não recebe a
                `BulkActionBar`) este é o único lugar da tela que diz quantos
                estão marcados. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleExportXlsx}
              disabled={filteredClients.length === 0}
              className="relative h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white disabled:opacity-40"
              aria-label={
                selectedCount > 0
                  ? `Exportar ${plural(selectedCount, 'selecionado', 'selecionados')}`
                  : 'Exportar'
              }
              title={selectedCount > 0 ? `Exportar (${selectedCount})` : 'Exportar'}
            >
              <FileSpreadsheet className="h-4 w-4" />
              {selectedCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold leading-none text-emerald-800">
                  {selectedCount}
                </span>
              )}
            </Button>

            {/* Importar da agenda: só no app nativo (o plugin de contatos não
                existe na web). Grátis — sem gate de plano. */}
            {canWrite && Capacitor.isNativePlatform() && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setImportOpen(true)}
                className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
                aria-label="Importar contatos da agenda"
                title="Importar da agenda"
              >
                <ContactRound className="h-4 w-4" />
              </Button>
            )}

            {canWrite && (
              /* A ação PRIMÁRIA mantém o texto — é ela que o excedente comeria
                 primeiro se tudo fosse texto, e é a que menos pode sumir. */
              <Button3D
                size="sm"
                color="light"
                className="shrink-0"
                onClick={() => navigate('/clients/new')}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Novo
              </Button3D>
            )}
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">{clientListTotalLabel(recorteAtivo)}</p>
            <p className="truncate text-3xl font-bold leading-tight">{summary.count}</p>
            <p className="mt-0.5 text-xs text-white/70">
              {summary.count === 0
                ? 'Nenhum cliente neste filtro'
                : `${plural(summary.withPhone, 'com telefone', 'com telefone')}`}
              {/* "no silent caps": quem não dá para chamar é DITO, senão o
                  cadastro incompleto some da vista e nunca é corrigido. */}
              {summary.withoutContact > 0 &&
                ` · ${plural(summary.withoutContact, 'sem contato', 'sem contato')}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e seleção.
              Eles são um grupo só (todos respondem "o que eu estou vendo"),
              então ficam juntos com `space-y-2` em vez do `space-y-5` que
              separa as SEÇÕES da tela (#191/#194). */}
          <div className="space-y-2">
            {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o
                campo é `min-w-0 flex-1`: sem isso o input empurraria os botões
                para fora da tela em vez de encolher (#186). */}
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Nome, telefone ou cidade"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Ordenação: menu, não modal — é uma escolha única entre quatro
                  rótulos curtos, e um diálogo para isso seria dois cliques a
                  mais pelo mesmo resultado. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    aria-label={`Ordenar (${CLIENT_SORT_LABELS[sortOption]})`}
                    title={`Ordenar: ${CLIENT_SORT_LABELS[sortOption]}`}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {CLIENT_SORT_ORDER.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => setSortOption(option)}
                      className="gap-2"
                    >
                      {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                          placeholder, o rótulo escolhido fica deslocado. */}
                      <span className="flex h-4 w-4 items-center justify-center">
                        {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                      </span>
                      {CLIENT_SORT_LABELS[option]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="icon"
                className="relative h-10 w-10 shrink-0 rounded-xl"
                onClick={() => setFiltersOpen(true)}
                aria-label={
                  activeModalFilters > 0
                    ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                    : 'Filtros'
                }
                title="Filtros"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeModalFilters > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {activeModalFilters}
                  </span>
                )}
              </Button>
            </div>

            {/* CONTATO — botões planos, não alternador. O estilo de pílula 3D
                azul (#25) fica reservado a troca de CONTEXTO; recorte não
                merece o peso visual de um alternador. A contagem vai no
                `title`/`aria-label`, onde não custa pixel (#191). */}
            <div className={FILTER_CHIP_ROW}>
              {CLIENT_CONTACT_FILTERS.map((option) => {
                const active = contactFilter === option.value;
                const count = contactCounts[option.value] ?? 0;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setContactFilter(option.value)}
                    aria-pressed={active}
                    aria-label={`${option.label} (${count})`}
                    title={`${option.label}: ${plural(count, 'cliente', 'clientes')}`}
                    className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Entrada e saída do MODO DE SELEÇÃO (#192).
                Fora dele a esquerda mostra a ORDENAÇÃO ativa — ela é a única
                decisão da tela que não se anuncia sozinha; dentro, a ordenação
                dá lugar à marcação de todos. Sair limpa a seleção: deixar itens
                marcados sem nenhuma caixa na tela faria a `BulkActionBar` falar
                de uma escolha invisível. */}
            {!loading && filteredClients.length > 0 && (
              <div className="flex items-center justify-between gap-2 px-1">
                {selectionMode ? (
                  /* `<label>` em volta do Checkbox do Radix alterna DUAS vezes
                     e vira no-op (#29) — o padrão do app é o par Checkbox +
                     área de texto com `onClick` próprio. */
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(checked) => toggleSelectAll(!!checked, filteredClients)}
                      aria-label="Selecionar todos os clientes"
                    />
                    <span
                      className="cursor-pointer text-sm text-muted-foreground"
                      onClick={() => toggleSelectAll(!allSelected, filteredClients)}
                    >
                      Selecionar todos ({filteredClients.length})
                    </span>
                  </div>
                ) : (
                  <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
                    <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{CLIENT_SORT_LABELS[sortOption]}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (selectionMode) clearSelection();
                    setSelectionMode((prev) => !prev);
                  }}
                  className="shrink-0 rounded-full px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  {selectionMode ? 'Concluir' : 'Selecionar'}
                </button>
              </div>
            )}

            {/* Lista — DENTRO do mesmo grupo `space-y-2` da busca/recorte, e
                não um `-mt-N` competindo com o `space-y-5` de fora: margem
                negativa num filho direto PERDE a briga de especificidade contra
                o `space-y-*` do container (#194). */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={cn(CONTENT_CARD, 'h-20 animate-pulse')} />
                ))}
              </div>
            ) : filteredClients.length > 0 ? (
              /* Duas colunas no desktop (mesmo padrão de `/quotes`), com
                 `grid-cols-1` explícito no celular para um nome sem espaços não
                 esticar a coluna e vazar a página (#185). */
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {filteredClients.map((client) => {
                  const selected = isSelected(client.id);
                  const cleanPhone = client.phone?.replace(/\D/g, '') || '';

                  const openWhatsApp = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (cleanPhone) {
                      window.open(`https://wa.me/${cleanPhone}`, '_blank');
                    }
                  };

                  const callPhone = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (cleanPhone) {
                      window.open(`tel:${cleanPhone}`, '_self');
                    }
                  };

                  const openMaps = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (client.address) {
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.address)}`,
                        '_blank',
                      );
                    }
                  };

                  return (
                    <div
                      key={client.id}
                      className={cn(
                        CONTENT_CARD,
                        'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
                        selected && 'ring-2 ring-primary',
                      )}
                      onClick={() => navigate(`/clients/${client.id}/edit`)}
                    >
                      {/* Coluna da ESQUERDA: identificação + conteúdo. Ela dita
                          a altura do card — o `items-center` do card faz a
                          coluna de ações centrar contra ELA, e não contra a
                          linha do nome, que muda de altura conforme quebra
                          (#189). */}
                      <div className="min-w-0 flex-1 space-y-2">
                        {/* LINHA DE IDENTIFICAÇÃO: seleção + selos. Tudo que
                            identifica mas não é o conteúdo mora aqui, o que
                            devolve a largura inteira ao nome logo abaixo. */}
                        {(selectionMode ||
                          (client as any)._offline_pending ||
                          client.document ||
                          client.municipio_nome) && (
                          <div className="flex flex-wrap items-center gap-2">
                            {selectionMode && (
                              <Checkbox
                                checked={selected}
                                onCheckedChange={() => toggleItem(client.id)}
                                // O card inteiro navega; o clique na seleção não
                                // pode subir, senão marcar abriria o cadastro.
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Selecionar ${client.name}`}
                              />
                            )}
                            {(client as any)._offline_pending && (
                              <span
                                className="inline-flex items-center gap-1 text-amber-600"
                                title="Aguardando sincronização"
                              >
                                <Clock className="h-3.5 w-3.5" />
                              </span>
                            )}
                            {client.document && (
                              <Badge
                                variant="outline"
                                className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 font-mono text-xs text-primary"
                              >
                                {formatDocument(client.document)}
                              </Badge>
                            )}
                            {client.municipio_nome && (
                              <Badge
                                variant="outline"
                                className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                              >
                                {client.municipio_nome}
                                {client.uf ? `/${client.uf}` : ''}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* `min-w-0` não é decoração (#175): sem ele um flex
                            item nasce com `min-width: auto` e um nome sem
                            espaços estica a linha para fora da viewport. E sem
                            `truncate`: é o NOME que distingue duas linhas da
                            mesma lista, e cortá-lo come justamente o que
                            informa. */}
                        <div className="min-w-0 space-y-0.5">
                          {/* A foto e o reconhecimento visual da linha, entao
                              ela fica ao lado do NOME. `shrink-0` para ela nao
                              ser espremida, e `min-w-0` no texto pelo motivo do
                              #175 — sem ele um nome sem espacos estica a linha
                              para fora da viewport. */}
                          <div className="flex min-w-0 items-center gap-2">
                            {client.photo_url && (
                              <StorageImage
                                src={client.photo_url}
                                alt=""
                                className="h-8 w-8 shrink-0 rounded-full object-cover"
                              />
                            )}
                            <p className="min-w-0 break-words font-bold">{client.name}</p>
                          </div>
                          {client.phone && (
                            <p className="break-words text-sm text-muted-foreground">
                              {client.phone}
                            </p>
                          )}
                          {/* Endereço como chip clicável → Google Maps. */}
                          {client.address && (
                            <button
                              onClick={openMaps}
                              className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">{client.address}</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Ações rápidas — um CONJUNTO, centrado na altura do card
                          pelo `items-center` dele (#189). */}
                      {client.phone && (
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            onClick={openWhatsApp}
                            className="flex h-10 w-10 items-center justify-center rounded-full text-green-600 transition-colors hover:bg-green-100"
                            aria-label="Abrir WhatsApp"
                          >
                            <WhatsAppIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={callPhone}
                            className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
                            aria-label="Ligar para cliente"
                          >
                            <Phone className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <Users className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="text-muted-foreground">
                  {recorteAtivo ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Importar contatos da agenda (só montado no app nativo) */}
      {Capacitor.isNativePlatform() && (
        <ImportContactsModal
          open={importOpen}
          onOpenChange={setImportOpen}
          existingClients={clients}
          userId={user.id}
          companyId={activeCompany?.id ?? null}
          onImported={() => {
            // Online recarrega do servidor; offline os clientes recém-enfileirados
            // já estão no cache local.
            if (isOnline) {
              fetchClients();
            } else {
              setClients((getCachedClients(user.id) || []) as Client[]);
            }
          }}
        />
      )}

      {/* Bulk Action Bar — bulk delete is owner/admin only. Selection for
          export still works via the Export button above for other roles. */}
      {canDelete && (
        <BulkActionBar
          selectedCount={selectedCount}
          totalCount={filteredClients.length}
          allSelected={allSelected}
          onSelectAll={(checked) => toggleSelectAll(checked, filteredClients)}
          onDelete={() => setBulkDeleteOpen(true)}
          onClear={clearSelection}
          isDeleting={bulkDeleting}
        />
      )}

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        itemType={selectedCount === 1 ? 'cliente' : 'clientes'}
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleting}
      />

      {/* Filtros: período de cadastro.
          O CONTATO aparece só na faixa de botões, e não aqui, porque ele já se
          anuncia sozinho na tela — repeti-lo no modal duplicaria o controle sem
          revelar nada que o usuário não esteja vendo. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte a agenda por quando o cliente foi cadastrado."
        groups={filterGroups}
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: EMPTY_LIST_RANGE }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && CLIENT_PERIOD_PRESETS.includes(wanted as ReportPeriodPreset)
              ? (wanted as ReportPeriodPreset)
              : null,
          );
        }}
      />
    </MainLayout>
  );
}
